// Live computation of the per-token-type multiplier shown next to each model
// in the admin pricing UI. Pure display helper — credit math always happens
// on the backend with the raw USD prices, never the multiplier.
//
// Why frontend-only: persisting the auto-derived value would drift the moment
// the baseline price (or the baseline model itself) changes. Computing live
// keeps a single source of truth — the prices — and the multiplier is just a
// view over them.

import type {
  GlobalPricingConfig,
  ModelPricing,
  TokenType,
} from "./apiTyped";

export const TOKEN_TYPES: TokenType[] = [
  "input",
  "output",
  "cachedRead",
  "cacheCreation",
  "thinking",
];

export const TOKEN_TYPE_LABELS: Record<TokenType, string> = {
  input: "Input",
  output: "Output",
  cachedRead: "Cached read",
  cacheCreation: "Cache write",
  thinking: "Thinking",
};

export type EffectiveMultiplier = {
  value: number;
  source: "override" | "auto";
};

export function effectiveMultiplier(
  model: ModelPricing,
  tokenType: TokenType,
  baseline: ModelPricing | null,
  global: GlobalPricingConfig | null,
): EffectiveMultiplier | null {
  const override = model.multiplierOverrides?.[tokenType];
  if (typeof override === "number" && Number.isFinite(override)) {
    return { value: override, source: "override" };
  }
  if (!baseline || !global?.baselineTokenType) return null;
  const basePrice = baseline.prices[global.baselineTokenType];
  if (!basePrice || basePrice <= 0) return null;
  const modelPrice = model.prices[tokenType];
  if (modelPrice == null) return null;
  return { value: modelPrice / basePrice, source: "auto" };
}

export function fmtMultiplier(m: EffectiveMultiplier | null): string {
  if (!m) return "—";
  if (m.value >= 100) return `${m.value.toFixed(0)}×`;
  if (m.value >= 10) return `${m.value.toFixed(1)}×`;
  return `${m.value.toFixed(2)}×`;
}

export function creditsPerUsd(global: GlobalPricingConfig | null): number {
  if (!global?.usdPerCredit || global.usdPerCredit <= 0) return 0;
  return 1 / global.usdPerCredit;
}
